import React, { useState, useEffect } from 'react';
import {
  Select,
  Input,
  Button,
  Text,
  Box,
  Flex,
  Textarea,
  FormLabel,
  Grid,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaCar, FaExclamationTriangle, FaBullhorn } from 'react-icons/fa';
import AppointmentVehicleDetailButton from './AppointmentVehicleDetailButton';
import AppointmentCustomerActions from './AppointmentCustomerActions';
import AvailableTimes from './AvailableTimes';
import {
  formatDateInput,
  isMaintenanceServiceType,
} from '../../utils/defaultMaintenanceService';
import {
  WARRANTY_TYPES,
  DEFAULT_WARRANTY_TYPE_CODE,
  isWarrantyServiceType,
} from '../../utils/warrantyTypes';
import {
  formatDurationLabel,
  formatDurationHhMm,
  parseDurationHhMm,
  sumServiceEventsMinutes,
} from '../../utils/serviceDuration';
import { filterServicesForVehicle } from '../../utils/filterServicesForVehicle';
import {
  getWorkTimeSlots,
  formatTimeForSelect,
} from '../../utils/workSchedule';
import VehicleHistoryModalComplete from '../Vehicles/VehicleHistoryModalComplete';
import ServiceAlertsModal from '../Scheduler/ServiceAlertsModal';
import { getData } from '../../utils/httpClient';
import { hasPendingAlerts } from '../../utils/vehicleAlerts';

const RECEPTION_TIME_SLOTS = getWorkTimeSlots(30);
const CALENDAR_HEIGHT = '40vh';

const ReadOnlyField = ({ label, value }) => {
  const bg = useColorModeValue('gray.100', 'whiteAlpha.200');
  return (
    <Box>
      <FormLabel fontSize="xs" mb={0.5} color="gray.600">
        {label}
      </FormLabel>
      <Input size="sm" value={value || '—'} isReadOnly bg={bg} />
    </Box>
  );
};

const Field = ({ label, children }) => (
  <Box>
    <FormLabel fontSize="xs" mb={0.5} color="gray.600">
      {label}
    </FormLabel>
    {children}
  </Box>
);

const HorasDisponiblesHeader = () => {
  const lineColor = useColorModeValue('gray.400', 'gray.500');
  return (
    <Flex align="center" gap={3} py={2} px={4} flexShrink={0}>
      <Box flex={1} h="1px" bg={lineColor} />
      <Text fontSize="sm" fontWeight="bold" letterSpacing="wider" whiteSpace="nowrap">
        HORAS DISPONIBLES
      </Text>
      <Box flex={1} h="1px" bg={lineColor} />
    </Flex>
  );
};

const ServiceDetails = ({
  newService,
  selectedVehicle,
  selectedCustomer,
  mechanicsList,
  serviceTypesList,
  servicesList,
  appointmentList,
  customerTransferList,
  holidayList,
  onAppointmentRangeChange,
  handleServiceChange,
  handleCancelService,
  handleSubmit,
  selectedDate,
  showByDate,
  handleToggleButtonDate,
  setSelectedDate,
  onOpenCampaigns,
  vehicleInfo,
  setVehicleInfo,
  onSearchVehicle,
  isSearchingVehicle,
  handleChangeCustomer,
}) => {
  const [filteredServices, setFilteredServices] = useState(servicesList);
  const [vehicleData, setVehicleData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasAlerts, setHasAlerts] = useState(false);
  const [manualScheduleTick, setManualScheduleTick] = useState(0);
  const [durationDraft, setDurationDraft] = useState('00:30');
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const { isOpen: isAlertsOpen, onOpen: onAlertsOpen, onClose: onAlertsClose } = useDisclosure();

  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.300');
  const readOnlyBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const selectProps = { size: 'sm', bg: 'white', w: '100%' };
  const hasVehicle = Boolean(selectedVehicle && selectedCustomer);
  // showByDate true = horario recomendado (auto). false = buscar por fecha (elige día).
  const isRecommendedMode = showByDate;
  const isDateSearchMode = !showByDate;

  const modelLabel =
    selectedVehicle?.model?.description ||
    (typeof selectedVehicle?.model === 'string' ? selectedVehicle.model : null) ||
    '—';

  useEffect(() => {
    setFilteredServices(filterServicesForVehicle(servicesList, selectedVehicle));
  }, [servicesList, selectedVehicle]);

  useEffect(() => {
    const check = async () => {
      if (!selectedVehicle?.id) {
        setHasAlerts(false);
        return;
      }
      try {
        const res = await getData(`/vehicleAlerts/vehicle/${selectedVehicle.id}`, {
          skipLoader: true,
        });
        setHasAlerts(hasPendingAlerts(res?.payload?.alerts || []));
      } catch {
        setHasAlerts(false);
      }
    };
    check();
  }, [selectedVehicle?.id, isAlertsOpen]);

  const refreshVehicleAlerts = async () => {
    if (!selectedVehicle?.id) {
      setHasAlerts(false);
      return;
    }
    try {
      const res = await getData(`/vehicleAlerts/vehicle/${selectedVehicle.id}`, {
        skipLoader: true,
      });
      setHasAlerts(hasPendingAlerts(res?.payload?.alerts || []));
    } catch {
      setHasAlerts(false);
    }
  };

  const handleSelectService = (serviceId) => {
    if (!serviceId) {
      handleServiceChange('serviceId', '');
      return;
    }
    const service = filteredServices.find(
      (s) => String(s.id) === String(serviceId),
    );
    if (!service) return;
    handleServiceChange('serviceId', service.id);
    handleServiceChange('description', service.name);
    handleServiceChange('service', service);
    const totalMinutes = sumServiceEventsMinutes(service.servicesEvents || []);
    handleServiceChange('estimatedTime', totalMinutes || 30);
    if (service.volkswagenCost != null && service.volkswagenCost !== '') {
      handleServiceChange('volkswagenCost', String(service.volkswagenCost));
    }
  };

  const openHistory = async () => {
    if (!selectedVehicle?.id) return;
    setLoadingHistory(true);
    try {
      const res = await getData(`/vehicles/${selectedVehicle.id}`);
      setVehicleData(res?.payload?.vehicle);
      onHistoryOpen();
    } finally {
      setLoadingHistory(false);
    }
  };

  const durationLabel = formatDurationLabel(newService.estimatedTime);
  const isMaintenanceType = isMaintenanceServiceType(
    newService.serviceTypeId,
    serviceTypesList,
  );
  const isWarrantyType = isWarrantyServiceType(
    newService.serviceTypeId,
    serviceTypesList,
  );
  const requiresService = isMaintenanceType;
  const isManualDuration =
    Boolean(newService.serviceTypeId) && !requiresService;

  useEffect(() => {
    if (!isManualDuration) return;
    setDurationDraft(formatDurationHhMm(newService.estimatedTime || 30));
    // Solo al cambiar tipo / modo; no pelear con lo que escribe el usuario
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualDuration, newService.serviceTypeId]);

  const canShowCalendar =
    hasVehicle &&
    newService.serviceTypeId &&
    (requiresService ? Boolean(newService.serviceId) : Number(newService.estimatedTime) > 0) &&
    newService.customerTransferId &&
    newService.parking;

  return (
    <Box h="100%" flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
      <Text
        textAlign="center"
        fontSize="lg"
        fontWeight="bold"
        py={2}
        flexShrink={0}
        borderBottom="1px solid"
        borderColor={borderColor}
      >
        Agendar Servicio
      </Text>

      {/* Formulario 4 columnas — estilo programa anterior */}
      <Box flex="1" minH={0} overflowY="auto" px={4} py={3}>
        <Grid templateColumns="repeat(4, 1fr)" gap={3} mb={3}>
          {/* Columna 1 */}
          <Box>
            <Field label="Vehículo">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Box as={FaCar} color="gray.600" />
                </InputLeftElement>
                <Input
                  pl={8}
                  pr="72px"
                  value={vehicleInfo?.searchData || ''}
                  onChange={(e) =>
                    setVehicleInfo({ searchData: e.target.value.toUpperCase() })
                  }
                  onKeyDown={(e) => e.key === 'Enter' && onSearchVehicle?.()}
                  placeholder="Patente"
                  bg="white"
                  textTransform="uppercase"
                />
                <InputRightElement w="72px" pr={1}>
                  <Flex align="center" gap={0}>
                    <IconButton
                      size="sm"
                      aria-label="Buscar vehículo"
                      icon={<SearchIcon />}
                      variant="ghost"
                      colorScheme="gray"
                      onClick={onSearchVehicle}
                      isLoading={isSearchingVehicle}
                    />
                    <AppointmentVehicleDetailButton
                      selectedVehicle={selectedVehicle}
                      selectedCustomer={selectedCustomer}
                    />
                  </Flex>
                </InputRightElement>
              </InputGroup>
            </Field>
            <Field label="Operario">
              <Select
                {...selectProps}
                placeholder="Seleccionar"
                isDisabled={!hasVehicle}
                onChange={(e) => handleServiceChange('mechanicId', e.target.value)}
                value={newService.mechanicId}
              >
                {mechanicsList?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Hora recepción">
              <Select
                {...selectProps}
                isDisabled={!hasVehicle}
                value={formatTimeForSelect(newService.time)}
                onChange={(e) => {
                  const t = e.target.value;
                  handleServiceChange('time', t ? `${t}:00` : '');
                  setManualScheduleTick((n) => n + 1);
                }}
              >
                <option value="">Seleccionar</option>
                {RECEPTION_TIME_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </Select>
            </Field>
          </Box>

          {/* Columna 2 */}
          <Box>
            <Field label="Dueño actual">
              <InputGroup size="sm">
                <Input
                  isReadOnly
                  value={selectedCustomer?.name || '—'}
                  bg={readOnlyBg}
                  pr="40px"
                />
                <InputRightElement w="40px">
                  <AppointmentCustomerActions
                    selectedCustomer={selectedCustomer}
                    selectedVehicle={selectedVehicle}
                    onChangeCustomer={handleChangeCustomer}
                  />
                </InputRightElement>
              </InputGroup>
            </Field>
            <Field label="Tipo servicio">
              <Select
                {...selectProps}
                placeholder="Seleccionar"
                isDisabled={!hasVehicle}
                onChange={(e) => handleServiceChange('serviceTypeId', e.target.value)}
                value={
                  newService.serviceTypeId
                    ? String(newService.serviceTypeId)
                    : ''
                }
              >
                {serviceTypesList?.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Traslado del cliente">
              <Select
                {...selectProps}
                placeholder="Seleccionar"
                isDisabled={!hasVehicle}
                onChange={(e) =>
                  handleServiceChange('customerTransferId', e.target.value)
                }
                value={newService.customerTransferId}
              >
                {customerTransferList?.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}
                  </option>
                ))}
              </Select>
            </Field>
          </Box>

          {/* Columna 3 */}
          <Box>
            <ReadOnlyField label="Modelo" value={modelLabel} />
            {isWarrantyType ? (
              <Field label="Tipo garantía">
                <Select
                  {...selectProps}
                  isDisabled={!hasVehicle}
                  value={
                    newService.warrantyType || DEFAULT_WARRANTY_TYPE_CODE
                  }
                  onChange={(e) =>
                    handleServiceChange('warrantyType', e.target.value)
                  }
                >
                  {WARRANTY_TYPES.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <Field label="Servicio">
                {!newService.serviceTypeId || !requiresService ? (
                  <Input
                    size="sm"
                    isReadOnly
                    value="—"
                    bg="gray.100"
                    isDisabled
                  />
                ) : (
                  <Select
                    {...selectProps}
                    isDisabled={!hasVehicle}
                    value={
                      newService.serviceId
                        ? String(newService.serviceId)
                        : ''
                    }
                    onChange={(e) => handleSelectService(e.target.value)}
                  >
                    <option value="">Seleccionar</option>
                    {filteredServices.map((service) => (
                      <option key={service.id} value={String(service.id)}>
                        {service.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <Field label="Estacionamiento">
              <Select
                {...selectProps}
                placeholder="Seleccionar"
                isDisabled={!hasVehicle}
                value={newService.parking || ''}
                onChange={(e) => handleServiceChange('parking', e.target.value)}
              >
                {[1, 2, 3].map((parking) => (
                  <option key={parking} value={String(parking)}>
                    {parking}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Presupuesto estimado">
              <Input
                {...selectProps}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                isDisabled={!hasVehicle}
                value={newService.volkswagenCost ?? ''}
                onChange={(e) =>
                  handleServiceChange('volkswagenCost', e.target.value)
                }
              />
            </Field>
          </Box>

          {/* Columna 4 */}
          <Box>
            <Field label={isDateSearchMode ? 'Fecha del turno' : 'Fecha desde'}>
              <Input
                {...selectProps}
                type="date"
                isDisabled={!hasVehicle || isRecommendedMode}
                value={newService.date || selectedDate || ''}
                min={formatDateInput(new Date())}
                onChange={(e) => {
                  handleServiceChange('date', e.target.value);
                  setSelectedDate(e.target.value);
                  setManualScheduleTick((n) => n + 1);
                }}
              />
            </Field>
            {requiresService || !newService.serviceTypeId ? (
              <ReadOnlyField label="Duración" value={durationLabel} />
            ) : (
              <Field label="Duración (hh:mm)">
                <Input
                  {...selectProps}
                  type="text"
                  inputMode="numeric"
                  placeholder="01:30"
                  value={durationDraft}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^\d:]/g, '');
                    setDurationDraft(next);
                    const mins = parseDurationHhMm(next, { strictMinutes: true });
                    if (mins != null) {
                      handleServiceChange('estimatedTime', mins);
                    }
                  }}
                  onBlur={() => {
                    const mins = parseDurationHhMm(durationDraft);
                    if (mins != null) {
                      setDurationDraft(formatDurationHhMm(mins));
                      handleServiceChange('estimatedTime', mins);
                    } else {
                      const fallback = Number(newService.estimatedTime) || 30;
                      setDurationDraft(formatDurationHhMm(fallback));
                      handleServiceChange('estimatedTime', fallback);
                    }
                  }}
                  isDisabled={!hasVehicle}
                />
              </Field>
            )}
            <Button
              size="sm"
              colorScheme="gray"
              w="100%"
              mt={5}
              onClick={handleToggleButtonDate}
              isDisabled={!hasVehicle}
            >
              {isRecommendedMode ? 'Buscar por fecha' : 'Horario recomendado'}
            </Button>
          </Box>
        </Grid>

        {/* Fila: Campañas | Avisos grande | Historial */}
        <Flex align="center" gap={4} mb={2} minH="90px">
          <Button
            colorScheme="gray"
            size="md"
            leftIcon={<FaBullhorn />}
            onClick={onOpenCampaigns}
            isDisabled={!hasVehicle}
            flexShrink={0}
          >
            Campañas
          </Button>

          <Flex flex={1} justify="center" align="center">
            <Button
              w="100px"
              h="90px"
              colorScheme={hasAlerts ? 'yellow' : 'gray'}
              variant={hasAlerts ? 'solid' : 'outline'}
              color={hasAlerts ? 'gray.800' : 'gray.600'}
              onClick={onAlertsOpen}
              isDisabled={!hasVehicle}
              flexDirection="column"
              gap={1}
              _hover={{ bg: hasAlerts ? 'yellow.400' : 'gray.100' }}
            >
              <Box as={FaExclamationTriangle} boxSize="36px" />
            </Button>
          </Flex>

          <Button
            size="md"
            variant="outline"
            leftIcon={<FaCar />}
            onClick={openHistory}
            isLoading={loadingHistory}
            isDisabled={!hasVehicle}
            flexShrink={0}
          >
            Historial
          </Button>
        </Flex>
      </Box>

      <HorasDisponiblesHeader />

      <Box
        flexShrink={0}
        h={CALENDAR_HEIGHT}
        maxH={CALENDAR_HEIGHT}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor={borderColor}
        overflow="hidden"
        bg="white"
      >
        {canShowCalendar ? (
          <Box
            h={CALENDAR_HEIGHT}
            sx={{
              '.rbc-calendar': { height: CALENDAR_HEIGHT, fontSize: '11px' },
              '.rbc-toolbar': { mb: 0, minHeight: '28px', flexWrap: 'wrap' },
              '.rbc-toolbar button': { fontSize: '11px', padding: '2px 6px' },
              '.rbc-time-view': { flex: 1, minHeight: 0 },
              '.rbc-time-content': { overflowY: 'auto' },
              '.rbc-slot-selection': {
                backgroundColor: 'rgba(255, 235, 59, 0.35) !important',
              },
              '.rbc-event': { fontSize: '10px' },
            }}
          >
            <AvailableTimes
              selectedDateString={newService.date || selectedDate || ''}
              selectedTimeString={newService.time || ''}
              duration={newService.estimatedTime || 30}
              serviceEvents={newService.service?.servicesEvents}
              searchByDate={isDateSearchMode}
              show
              appointmentList={appointmentList}
              handleServiceChange={handleServiceChange}
              holidayList={holidayList}
              selectedMechanicId={newService.mechanicId}
              manualScheduleTick={manualScheduleTick}
              calendarHeight={CALENDAR_HEIGHT}
              onAppointmentRangeChange={onAppointmentRangeChange}
            />
          </Box>
        ) : (
          <Flex
            h={CALENDAR_HEIGHT}
            align="center"
            justify="center"
            color="gray.500"
            fontSize="sm"
            px={4}
            textAlign="center"
          >
            {hasVehicle
              ? requiresService
                ? 'Complete tipo, servicio y traslado para ver horarios (operario opcional)'
                : 'Complete tipo, duración y traslado para ver horarios (operario opcional)'
              : 'Busque un vehículo para continuar'}
          </Flex>
        )}
      </Box>

      {/* Pie: petición + acciones */}
      <Grid
        templateColumns="1fr auto"
        gap={4}
        p={3}
        flexShrink={0}
        borderTop="1px solid"
        borderColor={borderColor}
        alignItems="end"
      >
        <Box>
          <FormLabel fontSize="xs" mb={1} color="gray.600">
            Petición cliente
          </FormLabel>
          <Textarea
            size="sm"
            rows={3}
            value={newService.observations}
            onChange={(e) => handleServiceChange('observations', e.target.value)}
            isDisabled={!hasVehicle}
            bg="white"
            resize="none"
          />
        </Box>
        <Flex gap={2} pb={1}>
          <Button size="md" variant="outline" onClick={handleCancelService}>
            Cancelar
          </Button>
          <Button
            size="md"
            colorScheme="gray"
            bg="#003064"
            color="white"
            _hover={{ bg: '#004080' }}
            _active={{ bg: '#00264F' }}
            onClick={handleSubmit}
            isDisabled={!hasVehicle}
          >
            Confirmar turno
          </Button>
        </Flex>
      </Grid>

      <VehicleHistoryModalComplete
        isOpen={isHistoryOpen}
        onClose={onHistoryClose}
        vehicleData={vehicleData}
        isLoading={loadingHistory}
        kilometersLabel="Kilometraje anterior"
      />
      <ServiceAlertsModal
        isOpen={isAlertsOpen}
        onClose={onAlertsClose}
        vehicleId={selectedVehicle?.id}
        onAlertsChanged={refreshVehicleAlerts}
      />
    </Box>
  );
};

export default ServiceDetails;
